import { SvgIcon } from "@progress/kendo-react-common";
import React, { useState, useEffect } from "react";
import { faHeartPulse } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { circleIcon } from "@progress/kendo-svg-icons";
import { SoftwareFeatures } from "../../../../../Zustand/accountSettingsStore";
import EndpointsHealthTable from "../Endpoints/EndpointsHealthTable";
import { TileLayout } from "@progress/kendo-react-layout";
import { Tooltip } from "@progress/kendo-react-tooltip";
import HeadUnitsHealthTable from "../HeadUnits/HeadUnitsHealthTable";
import AccountHealthTiles from "./AccountHealthTiles";
import GatewaysHealthTable from "../Gateways/GatewaysHealthTable";
import { enMessages } from "../../../../../messages/en-US";
import { useLocalization } from "@progress/kendo-react-intl";

interface Props {
  softwareFeatures: SoftwareFeatures;
  healthData: any;
  initialSelectedTile?: string | null;
}

export default function HealthTiles({ softwareFeatures, healthData, initialSelectedTile }: Props) {
  const [selectedTile, setSelectedTile] = React.useState<string | null>(initialSelectedTile || "account");
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  const localizationService = useLocalization();
  // Update selectedTile when initialSelectedTile prop changes
  useEffect(() => {
    if (initialSelectedTile !== undefined) {
      setSelectedTile(initialSelectedTile);
    }
  }, [initialSelectedTile]);

  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(window.innerWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Healthy':
      case 'Online':
        return 'text-gbBrand-green-700';
      case 'Unhealthy':
      case 'Offline':
        return 'text-gbBrand-red-500';
      case 'Warning':
        return 'text-gbBrand-yellow-500';
      default:
        return 'text-gbBrand-blue-500';
    }
  };

  const metricsItems = [
    {
      header: "Account",
      body: (
        <div className={`p-4 shadow-sm pointer transition-all duration-150 rounded-md border-b-4 ${selectedTile === "account" ? 'tile-selected border-b-[#ff6357]' : ''}`}
        style={{
          width: '100%',
          height: '100%',
          cursor: 'pointer',
          background: 'transparent'
        }}
        onClick={() => handleTileClick("account")}>
          <div className="flex items-center gap-2 font-bold">
            <div>{localizationService.toLanguageString('custom.accountSetup', enMessages.custom.accountSetup)}</div>
          </div>
          <div className="flex flex-col items-start mt-2 gap-2">
            <Tooltip 
              anchorElement="target" 
              position="bottom"
              parentTitle={true}
            >
              <div className="flex items-center gap-1" title={`Health: ${healthData.accountMetrics.health.status}`}>
                <FontAwesomeIcon icon={faHeartPulse} size="2xl"/>
                <SvgIcon icon={circleIcon} size="xxlarge" className={getStatusColor(healthData.accountMetrics.health.status)} />
              </div>
            </Tooltip>
          </div>
        </div>
      ),
      reorderable: false,
    },
    {
      header: "Endpoints",
      body: (
        <div
          className={`p-4 shadow-sm pointer transition-all duration-150 rounded-md border-b-4 ${selectedTile === "endpoints" ? 'tile-selected border-b-[#ff6357]' : ''}`}
          style={{
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            background: 'transparent'
          }}
          onClick={() => handleTileClick("endpoints")}
        >
          <div className="flex items-center gap-2 font-bold">
            <div>{localizationService.toLanguageString('custom.endpoints', enMessages.custom.endpoints)}</div>
          </div>
          <div className="flex flex-col items-start mt-2 gap-2">
            <Tooltip 
              anchorElement="target" 
              position="bottom"
              parentTitle={true}
            >
              <div className="flex items-center gap-1" title={`${localizationService.toLanguageString('custom.health', enMessages.custom.health)}: ${healthData.endpointMetrics.health.status}`}>
                <FontAwesomeIcon icon={faHeartPulse} size="2xl"/>
                <SvgIcon icon={circleIcon} size="xxlarge" className={getStatusColor(healthData.endpointMetrics.health.status)} />
              </div>
            </Tooltip>
          </div>
        </div>
      ),
      reorderable: false,
    },
    {
      header: "Head Units",
      body: (
        <div
          className={`p-4 shadow-sm pointer transition-all duration-150 rounded-md border-b-4 ${selectedTile === "headUnits" ? 'tile-selected border-b-[#ff6357]' : ''}`}
          style={{
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            background: 'transparent'
          }}
          onClick={() => handleTileClick("headUnits")}
        >
          <div className="flex items-center gap-2 font-bold">
            <div>{localizationService.toLanguageString('custom.headUnits', enMessages.custom.headUnits)}</div>
          </div>
          <div className="flex flex-col items-start mt-2 gap-2">
            <Tooltip 
              anchorElement="target" 
              position="bottom"
              parentTitle={true}
            >
              <div className="flex items-center gap-1" title={`${localizationService.toLanguageString('custom.health', enMessages.custom.health)}: ${healthData.headUnitMetrics.health.status}`}>
                <FontAwesomeIcon icon={faHeartPulse} size="2xl"/>
                <SvgIcon icon={circleIcon} size="xxlarge" className={getStatusColor(healthData.headUnitMetrics.health.status)} />
              </div>
            </Tooltip>
          </div>
        </div>
      ),
      reorderable: false,
    },
    {
      header: "Gateways",
      body: (
        <div
          className={`p-4 shadow-sm pointer transition-all duration-150 rounded-md border-b-4 ${selectedTile === "gateways" ? 'tile-selected border-b-[#ff6357]' : ''}`}
          style={{
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            background: 'transparent'
          }}
          onClick={() => handleTileClick("gateways")}
        >
          <div className="flex items-center gap-2 font-bold">
            <div>{localizationService.toLanguageString('custom.gateways', enMessages.custom.gateways)}</div>
          </div>
          <div className="flex flex-col items-start mt-2 gap-2">
            <Tooltip 
              anchorElement="target" 
              position="bottom"
              parentTitle={true}
            >
              <div className="flex items-center gap-1" title={`${localizationService.toLanguageString('custom.health', enMessages.custom.health)}: ${healthData.gatewayMetrics.health.status}`}>
                <FontAwesomeIcon icon={faHeartPulse} size="2xl"/>
                <SvgIcon icon={circleIcon} size="xxlarge" className={getStatusColor(healthData.gatewayMetrics.health.status)} />
              </div>
            </Tooltip>
          </div>
        </div>
      ),
      reorderable: false,
    }
  ];

  // Filter tiles based on softwareFeatures
  const filteredMetricsItems = metricsItems.filter(tile => {
    if (tile.header === 'Endpoints' && softwareFeatures.isAutomaticSystemEnabled === false) return false;
    if (tile.header === 'Head Units' && softwareFeatures.isManualSystemEnabled === false) return false;
    return true;
  });

  const handleTileClick = (tileId: string) => {
    setSelectedTile(tileId);
  };

  const getSelectedContent = () => {
    switch(selectedTile) {
      case "endpoints":
        return <EndpointsHealthTable healthData={healthData.endpointMetrics?.devices || []} />;
      case "headUnits":
        return <HeadUnitsHealthTable healthData={healthData.headUnitMetrics?.devices || []} />;
      case "gateways":
        return <GatewaysHealthTable healthData={healthData.gatewayMetrics?.devices || []} />
      case "account":
          return <AccountHealthTiles healthData={healthData} softwareFeatures={softwareFeatures} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: containerWidth < 768 ? '1fr' : containerWidth < 1000 ? 'repeat(2, 1fr)' : 'repeat(6, 1fr)',
        gap: '10px',
        width: '100%'
      }}>
        {filteredMetricsItems.map((item, index) => (
          <div 
            key={index} 
            style={{ 
              width: '100%', 
              height: '100px',
              gridColumn: containerWidth >= 1000 ? `span ${6 / 4}` : 'span 1',
              marginBottom: '15px'
            }}
          >
            {item.body}
          </div>
        ))}
      </div>
      {selectedTile && (
        <TileLayout
          className="dashboard-page-tile dashboard-analysis-tab-tiles tile-content-layout"
          columns={1}
          rowHeight="auto"
          gap={{ rows: 10 }}
          items={[{
            header: "",
            body: getSelectedContent(),
            reorderable: false,
          }]}
        />
      )}
    </>
  );
}
